import React, { useState } from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Form, Input, Row, Col, Tooltip, Button, Alert } from 'antd';
import { connect } from 'dva';
import CryptoJS from 'crypto-js';
import StepComponent from '../../CredentialsForm/InfoSteps';
import ScanRunningInstances from '../../CredentialsForm/ScanRunningInstances';
import TestConnectionContent from '../ConfigurationTab/Components/TestConnectionContent';
import classes from './ReconnectGCPCloudAccount.module.css';
import { decryptAES, encryptAES } from '@/utils/utils';
import {
  azureAdvancedClientAndTenantIdSteps,
  azureAdvancedSecretKeyAndSubscriptionIdSteps,
  azureBasicClientAndTenantIdSteps,
  azureBasicSecretKeyAndSubscriptionIdSteps,
} from '@/utils/onBoardingSteps';

const { Item: FormItem } = Form;

const InfoSteps = ({ protectionLevel, props, credentials, setCredentials }) => {
  const [form] = Form.useForm();

  const onFormValuesChange = changedValues => {
    setCredentials({
      ...credentials,
      ...changedValues,
    });
  };

  return (
    <>
      <Alert
        message="Reconnect Cloud Account"
        description=" Please make sure that you have deleted the App that you have connected your Azure account with Cloudnosys, before proceeding with the following steps to reconnect."
        type="warning"
        showIcon
        style={{ marginBottom: '30px' }}
      />
      {protectionLevel === 'basic' && (
        <div>
          <StepComponent
            heading="Create Application (client) ID & Tenant ID"
            steps={azureBasicClientAndTenantIdSteps}
          />
          <StepComponent
            heading="Create Secret key ID & Subscription ID"
            steps={azureBasicSecretKeyAndSubscriptionIdSteps}
          />
        </div>
      )}
      {protectionLevel === 'advanced' && (
        <div>
          <StepComponent
            heading="Create Application (client) ID & Tenant ID"
            steps={azureAdvancedClientAndTenantIdSteps}
          />
          <StepComponent
            heading="Create Secret key ID & Subscription ID"
            steps={azureAdvancedSecretKeyAndSubscriptionIdSteps}
          />
        </div>
      )}
      <Form
        form={form}
        layout="horizontal"
        hideRequiredMark
        initialValues={credentials}
        onValuesChange={onFormValuesChange}
      >
        <h5 style={{ marginBottom: '25px', color: 'black' }}>Enter your Azure Credentials</h5>
        <div style={{ padding: '1rem 2rem', background: '#FAFAFA' }}>
          <Row gutter={12}>
            <Col span={12}>
              <p style={{ margin: 0 }}>AppId/clientId</p>
              <FormItem name="AppId">
                <Input
                  placeholder="App Id"
                  suffix={
                    <Tooltip title="Please find the AppId/clientId from your Azure Console and paste it here">
                      <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                    </Tooltip>
                  }
                />
              </FormItem>
            </Col>
            <Col span={12}>
              <p style={{ margin: 0 }}>Domain/Tenant ID</p>
              <FormItem name="TenantID">
                <Input
                  placeholder="Tenant ID"
                  suffix={
                    <Tooltip title="Please find the domain/Tenant ID from your Azure Console and paste it here">
                      <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                    </Tooltip>
                  }
                />
              </FormItem>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <p style={{ margin: 0 }}>SecretKey/Pwd</p>
              <FormItem name="secret">
                <Input
                  placeholder="Secret Key"
                  suffix={
                    <Tooltip title="Please find the SecretKey/Pwd from your Azure Console and paste it here">
                      <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                    </Tooltip>
                  }
                />
              </FormItem>
            </Col>
            <Col span={12}>
              <p style={{ margin: 0 }}>Subscription ID</p>
              <FormItem name="SubscriptionID">
                <Input
                  placeholder="Subscription ID"
                  suffix={
                    <Tooltip title="Please find the Subscription ID from your Azure Console and paste it here">
                      <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                    </Tooltip>
                  }
                />
              </FormItem>
            </Col>
          </Row>
        </div>
      </Form>
    </>
  );
};

const ReconnectAzureCloudAccount = props => {
  const {
    scanRunningInstances,
    cloudAccountData,
    testConnectionTypes,
    allCloudAccountsList,
    userName,
    updating,
    onClose,
    isForceFullyReconnecting,
    updateProtecionLevelTo = cloudAccountData.protectionLevel || 'basic',
  } = props;
  const { protectionLevel } = props.cloudAccountData;
  const [showConnectionTesting, setShowConnectionTesting] = useState(false);
  const [updatingAccount, setUpdatingAccount] = useState(false);

  const [credentials, setCredentials] = useState({
    SubscriptionID: '',
    AppId: '',
    secret: '',
    TenantID: '',
  });

  const isAdvancedAcc = updateProtecionLevelTo.toLowerCase() === 'advanced';

  const handleFinishReconnectAndScan = async () => {
    const payload = {
      ...cloudAccountData,
    };

    const { provider, orgId, id: cloudAccountId } = cloudAccountData;
    const { dispatch, userName } = props;

    // ** update the cloud account data
    await dispatch({
      type: 'cloudAccount/update',
      payload: {
        params: {
          type: 'edit',
          provider,
          orgId,
          id: cloudAccountId,
        },
        data: {
          protectionLevel,
          updatedAt: new Date(),
          updatedBy: userName,
          isActive: true,
        },
      },
    });

    // ** start scan
    runScan(payload);

    // ** close modal
    onClose();
  };

  const runScan = accountData => {
    const { dispatch } = props;

    // Get currently scanning cloud accounts Ids
    let currentlyScanningCloudAccountsIds = getCurrentlyScanningCloudAccountsIds({
      shouldFilterWithStartScanStatus: true,
    });

    // Reset scan progress if currently no cloud account is scanning
    if (currentlyScanningCloudAccountsIds.length === 0) {
      dispatch({
        type: 'cloudAccount/clearScanProgress',
      });
    }

    dispatch({
      type: 'scan/runScan',
      payload: {
        cloudAccounts: [accountData],
        orgId: accountData.orgId,
        currentlyScanningCloudAccountsIds,
      },
    });

    dispatch({
      type: 'scopeReport/current',
    });
  };

  const getCurrentlyScanningCloudAccountsIds = ({ shouldFilterWithStartScanStatus = true }) => {
    const { allCloudAccountsList } = props;

    // Get currently scanning cloud accounts Ids
    const currentlyScanningCloudAccountsIds = allCloudAccountsList
      .filter(
        cloudAccount =>
          cloudAccount.status === 'running-scan' ||
          (shouldFilterWithStartScanStatus && cloudAccount.status === 'start-scan')
      )
      .map(cloudAccount => cloudAccount.id);

    return currentlyScanningCloudAccountsIds;
  };

  const isAllConnectionConnected = () =>
    testConnectionTypes &&
    Object.keys(testConnectionTypes).every(key => testConnectionTypes[key].status === 200);

  const updateAccountAndTestConnection = async () => {
    setUpdatingAccount(true);
    const { dispatch } = props;

    const azureCredentials = {
      clientId: credentials.AppId,
      secret: credentials.secret,
      domain: credentials.TenantID,
      subscriptionId: credentials.SubscriptionID,
    };

    let userkeys = CryptoJS.AES.encrypt(
      JSON.stringify(azureCredentials),
      REACT_APP_SECRET
    ).toString();

    const payloadData = {
      cloudXrayConfig: {
        ...cloudAccountData.cloudXrayConfig,
        scanRunningInstances,
        updatedAt: new Date(),
      },
      userkeys,
    };

    await dispatch({
      type: 'cloudAccount/update',
      payload: {
        params: {
          type: 'edit',
          provider: cloudAccountData.provider,
          orgId: cloudAccountData.orgId,
          id: cloudAccountData.id,
        },
        data: payloadData,
      },
    });

    setUpdatingAccount(false);
    setShowConnectionTesting(true);
  };
  const isFormValid = () => {
    return Object.values(credentials).every(value => value);
  };
  return (
    <>
      {!showConnectionTesting && (
        <>
          <InfoSteps
            protectionLevel={protectionLevel}
            props={props}
            credentials={credentials}
            setCredentials={setCredentials}
          />
          <div
            style={{
              margin: '2rem 0',
            }}
          >
            {protectionLevel === 'advanced' && <ScanRunningInstances />}
          </div>

          <span
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              marginTop: '10px',
            }}
          >
            <Button
              type="primary"
              htmlType="button"
              loading={updatingAccount}
              onClick={() => {
                updateAccountAndTestConnection();
              }}
              disabled={!isFormValid()}
            >
              Test Connection
            </Button>
          </span>
        </>
      )}

      {showConnectionTesting && (
        <>
          <TestConnectionContent
            cloudAccountData={props.cloudAccountData}
            byPassAndTestAll={true}
            isEditCloudAccount={isForceFullyReconnecting ? false : true}
            connectionProtectionType={isAdvancedAcc ? 'advanced' : 'basic'}
          />

          <Button
            type="primary"
            style={{ float: 'right' }}
            disabled={!isAllConnectionConnected()}
            onClick={handleFinishReconnectAndScan}
            loading={props.updating}
          >
            Finish Reconnect & Scan
          </Button>
        </>
      )}
    </>
  );
};

export default connect(({ user, cloudAccount }) => ({
  updating: cloudAccount.updating,
  scanRunningInstances: cloudAccount.scanRunningInstances,
  testConnectionTypes: cloudAccount.testConnectionTypes,
  allCloudAccountsList: cloudAccount.allCloudAccountsList,
  userName: user.currentUser.name,
}))(ReconnectAzureCloudAccount);
